import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiCall } from '../../api/apiConfig';

// Async thunk for computing single embedding
export const computeEmbedding = createAsyncThunk(
  'semanticSimilarity/computeEmbedding',
  async ({ text }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/compute-embedding', 'POST', { text });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error computing embedding');
    }
  }
);

// Async thunk for batch embedding computation
export const computeBatchEmbeddings = createAsyncThunk(
  'semanticSimilarity/computeBatchEmbeddings',
  async ({ texts }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/compute-embeddings-batch', 'POST', { texts });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error in batch embedding computation');
    }
  }
);

// Async thunk for calculating similarity between two texts
export const calculateSimilarity = createAsyncThunk(
  'semanticSimilarity/calculateSimilarity',
  async ({ text1, text2 }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/calculate-similarity', 'POST', { text1, text2 });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error calculating similarity');
    }
  }
);

// Async thunk for finding most similar text
export const findMostSimilar = createAsyncThunk(
  'semanticSimilarity/findMostSimilar',
  async ({ query_text, candidate_texts, top_k }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/find-most-similar', 'POST', { 
        query_text, 
        candidate_texts, 
        top_k 
      });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error finding most similar text');
    }
  }
);

// Async thunk for clustering texts
export const clusterTexts = createAsyncThunk(
  'semanticSimilarity/clusterTexts',
  async ({ texts, similarity_threshold }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/cluster-texts', 'POST', { 
        texts, 
        similarity_threshold 
      });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error clustering texts');
    }
  }
);

const semanticSimilaritySlice = createSlice({
  name: 'semanticSimilarity',
  initialState: {
    // Single embedding results
    embedding: null,
    dimension: 0,
    method: null,
    
    // Batch embedding results
    batchResults: [],
    totalProcessed: 0,
    successful: 0,
    failed: 0,
    
    // Similarity calculation results
    similarityScore: 0,
    similarityPercentage: 0,
    text1: '',
    text2: '',
    
    // Similarity search results
    searchResults: [],
    queryText: '',
    totalCandidates: 0,
    
    // Clustering results
    clusters: [],
    totalTexts: 0,
    validTexts: 0,
    similarityThreshold: 0.7,
    
    // General state
    loading: false,
    error: null,
    currentMode: 'single', // 'single', 'batch', 'similarity', 'search', 'clustering'
    note: null,
  },
  reducers: {
    clearResults: (state) => {
      state.embedding = null;
      state.dimension = 0;
      state.method = null;
      state.batchResults = [];
      state.totalProcessed = 0;
      state.successful = 0;
      state.failed = 0;
      state.similarityScore = 0;
      state.similarityPercentage = 0;
      state.text1 = '';
      state.text2 = '';
      state.searchResults = [];
      state.queryText = '';
      state.totalCandidates = 0;
      state.clusters = [];
      state.totalTexts = 0;
      state.validTexts = 0;
      state.error = null;
      state.note = null;
    },
    setMode: (state, action) => {
      state.currentMode = action.payload;
    },
    setSimilarityThreshold: (state, action) => {
      state.similarityThreshold = action.payload;
    },
    updateEmbedding: (state, action) => {
      state.embedding = action.payload.embedding;
      state.dimension = action.payload.dimension;
      state.method = action.payload.method;
    },
  },
  extraReducers: (builder) => {
    builder
      // Compute embedding
      .addCase(computeEmbedding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(computeEmbedding.fulfilled, (state, action) => {
        state.loading = false;
        state.embedding = action.payload.embedding;
        state.dimension = action.payload.dimension;
        state.method = action.payload.method;
        state.note = action.payload.note;
      })
      .addCase(computeEmbedding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to compute embedding';
      })
      
      // Batch embeddings
      .addCase(computeBatchEmbeddings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(computeBatchEmbeddings.fulfilled, (state, action) => {
        state.loading = false;
        state.batchResults = action.payload.results || [];
        state.totalProcessed = action.payload.total_processed || 0;
        state.successful = action.payload.successful || 0;
        state.failed = action.payload.failed || 0;
      })
      .addCase(computeBatchEmbeddings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to compute batch embeddings';
      })
      
      // Calculate similarity
      .addCase(calculateSimilarity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateSimilarity.fulfilled, (state, action) => {
        state.loading = false;
        state.text1 = action.payload.text1;
        state.text2 = action.payload.text2;
        state.similarityScore = action.payload.similarity_score;
        state.similarityPercentage = action.payload.similarity_percentage;
        state.method = action.payload.method;
        state.note = action.payload.note;
      })
      .addCase(calculateSimilarity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to calculate similarity';
      })
      
      // Find most similar
      .addCase(findMostSimilar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(findMostSimilar.fulfilled, (state, action) => {
        state.loading = false;
        state.queryText = action.payload.query_text;
        state.totalCandidates = action.payload.total_candidates;
        state.searchResults = action.payload.top_results || [];
        state.method = action.payload.method;
        state.note = action.payload.note;
      })
      .addCase(findMostSimilar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to find most similar text';
      })
      
      // Cluster texts
      .addCase(clusterTexts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clusterTexts.fulfilled, (state, action) => {
        state.loading = false;
        state.totalTexts = action.payload.total_texts;
        state.validTexts = action.payload.valid_texts;
        state.clusters = action.payload.clusters || [];
        state.similarityThreshold = action.payload.similarity_threshold;
        state.method = action.payload.method;
        state.note = action.payload.note;
      })
      .addCase(clusterTexts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to cluster texts';
      });
  },
});

// Reusable selectors for state access
export const selectEmbedding = (state) => state.semanticSimilarity.embedding;
export const selectDimension = (state) => state.semanticSimilarity.dimension;
export const selectMethod = (state) => state.semanticSimilarity.method;
export const selectBatchResults = (state) => state.semanticSimilarity.batchResults;
export const selectTotalProcessed = (state) => state.semanticSimilarity.totalProcessed;
export const selectSuccessful = (state) => state.semanticSimilarity.successful;
export const selectFailed = (state) => state.semanticSimilarity.failed;
export const selectSimilarityScore = (state) => state.semanticSimilarity.similarityScore;
export const selectSimilarityPercentage = (state) => state.semanticSimilarity.similarityPercentage;
export const selectText1 = (state) => state.semanticSimilarity.text1;
export const selectText2 = (state) => state.semanticSimilarity.text2;
export const selectSearchResults = (state) => state.semanticSimilarity.searchResults;
export const selectQueryText = (state) => state.semanticSimilarity.queryText;
export const selectTotalCandidates = (state) => state.semanticSimilarity.totalCandidates;
export const selectClusters = (state) => state.semanticSimilarity.clusters;
export const selectTotalTexts = (state) => state.semanticSimilarity.totalTexts;
export const selectValidTexts = (state) => state.semanticSimilarity.validTexts;
export const selectSimilarityThreshold = (state) => state.semanticSimilarity.similarityThreshold;
export const selectLoading = (state) => state.semanticSimilarity.loading;
export const selectError = (state) => state.semanticSimilarity.error;
export const selectCurrentMode = (state) => state.semanticSimilarity.currentMode;
export const selectNote = (state) => state.semanticSimilarity.note;

// Export actions
export const { clearResults, setMode, setSimilarityThreshold, updateEmbedding } = semanticSimilaritySlice.actions;

export default semanticSimilaritySlice.reducer; 